import React from "react";
import { Button, Card, Collapse } from "react-bootstrap";
import { FiChevronDown } from "react-icons/fi";
import { Part } from "./ProjectConfigurationTypes.ts";
import TaskComponent from "./TaskComponent.tsx";

interface PartComponentProps {
  part: Part;
  phaseIndex: number;
  partIndex: number;
  partId: string;
  isExpanded: boolean;
  projectId: string;
  subjects: any[];
  topicsCache: Record<string, any[]>;
  contentStatus?: {
    video?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    notes?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    mcq?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    coding?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    project_coding?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  } | null;
  dragHandleProps: any;
  onToggleExpand: () => void;
  onPreviewContent: (taskIndex: number, subtaskIndex: number, type: "video" | "notes", content: any) => void;
  onFetchTopics: (subjectId: string) => Promise<void>;
  onTaskToggleExpand: (taskId: string) => void;
  isTaskExpanded: (taskId: string) => boolean;
  isSubtaskExpanded: (subtaskId: string) => boolean;
  toggleSubtaskExpand: (subtaskId: string) => void;
}

/**
 * PartComponent: Renders a part card with drag-and-drop support for tasks
 * This component displays a single part within a phase and manages its tasks.
 * It provides functionality for:
 * - Expanding/collapsing the part
 * - Editing the part name
 * - Adding new tasks
 * - Deleting the part
 * - Drag-and-drop reordering of tasks
 * - Rendering TaskComponent children for each task
 */
const PartComponent: React.FC<PartComponentProps> = ({
  part,
  phaseIndex,
  partIndex,
  partId,
  isExpanded,
  projectId,
  subjects,
  topicsCache,
  contentStatus,
  dragHandleProps,
  onToggleExpand,
  onPreviewContent,
  onFetchTopics,
  onTaskToggleExpand,
  isTaskExpanded,
  isSubtaskExpanded,
  toggleSubtaskExpand
}) => {
  return (
    <Card className="mb-2">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">

          <Button
            variant="light"
            size="sm"
            className="p-1"
            onClick={onToggleExpand}
          >
            <FiChevronDown
              size={18}
              style={{
                transition: "transform 0.2s",
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)"
              }}
            />
          </Button>
          <span className="fw-bold me-2">{part.id}</span>
          <span>{part.name || "—"}</span>
        </div>
       
      </Card.Header>
      <Collapse in={isExpanded}>
        <Card.Body>
          {part.tasks.map((task, taskIndex) => {
            const taskId = `${partId}-${task.id}`;
            return (
              <div key={task.id}>
                <TaskComponent
                  task={task}
                  phaseIndex={phaseIndex}
                  partIndex={partIndex}
                  taskIndex={taskIndex}
                  taskId={taskId}
                  isExpanded={isTaskExpanded(taskId)}
                  projectId={projectId}
                  subjects={subjects}
                  topicsCache={topicsCache}
                  contentStatus={contentStatus}
                  dragHandleProps={undefined}
                  onToggleExpand={() => onTaskToggleExpand(taskId)}
                  onPreviewContent={(subtaskIndex, type, content) => onPreviewContent(taskIndex, subtaskIndex, type, content)}
                  onFetchTopics={onFetchTopics}
                  isSubtaskExpanded={isSubtaskExpanded}
                  toggleSubtaskExpand={toggleSubtaskExpand}
                />
              </div>
            );
          })}
        </Card.Body>
      </Collapse>
    </Card>
  );
};

export default PartComponent;

